import React, { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { EventInfo } from '@/api/events/types/types';
import { LeftSideTabBar } from '@/components/LeftSideTabBar';
import { LineSeparator } from '@/components/LineSeparator';
import { Loading } from '@/components/Loading';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { DEFAULT_EVENT_CARD_LIST_HEIGHT, EventCardsList } from '@/feature/lobby/components/EventCardsList';
import { PlayerItem } from '@/feature/lobby/components/PlayerItem';
import { PlayersFilters } from '@/feature/lobby/components/PlayersFilters';
import { PlayerTileRow } from '@/feature/lobby/components/tile/PlayerTileRow';
import { usePickSelection } from '@/feature/lobby/hooks/use-pick-selection';
import { FILTER_MODES_TABS, FilterType, Filters, UFC_FILTER_MODES_TABS } from '@/feature/lobby/utils/filters';
import { useOpenPlayerProfile } from '@/feature/player-profile-sheets/hooks/use-open-player-profile-sheet';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';

import { DEDICATED_EVENT_CARD_LIST_HEIGHT, DedicatedEventCardsList } from '../components/DedicatedEventCardsList';
import { usePlayerFilters } from './use-player-filters';

/**
 * Hook for exposing methods to render league specific content
 *     - renderPlayerItem - renders the player item based on the filter type
 *     - renderLeagueComponents - renders the league components like tabs, filters, events
 *     - renderLeagueFooter - renders the footer message based on the filter type
 * @param league - the league for which the content is to be rendered
 * @param selectedFilter - Projection | Players
 * @param selectFilter - callback to change the filter method
 * @param sortedEvents - sorted events for the league
 * @param sortedPlayers - sorted players for the league
 * @param eventsLoading - loading state of the events
 * @param hideGameTime - optional flag to hide game time in the player item
 */
export const useLeagueContentRenderer = ({
    league,
    selectedFilter,
    selectFilter,
    sortedEvents,
    sortedPlayers,
    eventsLoading,
    hideGameTime,
    openSinglePlayerProfile,
}: {
    league: League;
    selectedFilter: FilterType;
    selectFilter: (filter: FilterType) => void;
    sortedPlayers: PlayerWithTeamAndEvent[];
    sortedEvents: EventInfo[];
    eventsLoading: boolean;
    hideGameTime?: boolean;
    openSinglePlayerProfile?: (player: PlayerWithTeamAndEvent, playerProjectionFilter?: string) => void;
}) => {
    const { playerProjectionFilter, resetAllFilters } = usePlayerFilters();

    const { makeSelection, removeSelection } = usePickSelection();

    const { t } = useTranslation('betslip_pickem');

    const filterModes = league === League.Ufc ? UFC_FILTER_MODES_TABS : FILTER_MODES_TABS;

    const setTab = useCallback(
        (tabId: string) => {
            selectFilter(filterModes.find(tab => tab.id === tabId)?.filterType ?? filterModes[0].filterType);
            resetAllFilters();
        },
        [selectFilter, filterModes, resetAllFilters]
    );

    const renderLeagueFooter = useCallback(() => {
        if (eventsLoading) {
            return (
                <Box m={'s46'}>
                    <Loading />
                </Box>
            );
        }
        //While changing the leagues the selected filter type will be Projection but the playerProjectionFilter will be undefined
        //until the new default selected filter is set
        const changingLeagues =
            eventsLoading && selectedFilter === Filters.Projection && playerProjectionFilter === undefined;
        //Avoid showing the no projection available message while changing leagues or data loading
        if (sortedPlayers.length === 0 && !eventsLoading && !changingLeagues) {
            return (
                <Box py={'s16'}>
                    <Text fontWeight="bold" textAlign={'center'}>
                        {t('no_projection_available')}
                    </Text>
                </Box>
            );
        }
        return null;
    }, [eventsLoading, selectedFilter, playerProjectionFilter, sortedPlayers.length, t]);

    const renderFilters = useCallback(() => {
        const initialIndex = filterModes.findIndex(tab => tab.filterType === selectedFilter);
        return (
            <>
                <LeftSideTabBar
                    tabs={filterModes}
                    initialIndex={initialIndex}
                    setSelectedTab={setTab}
                    location="event"
                />
                <PlayersFilters leagueId={league} filterType={selectedFilter} events={sortedEvents} />
            </>
        );
    }, [filterModes, league, selectedFilter, setTab, sortedEvents]);

    const renderEventCards = useCallback(() => {
        if (league === League.Ufc) {
            //There are no event cards for non team events
            return null;
        }
        if (league === League.Pga) {
            return (
                <MaxWidthWrapper minHeight={DEDICATED_EVENT_CARD_LIST_HEIGHT}>
                    <DedicatedEventCardsList sortedEvents={sortedEvents} loading={eventsLoading} />
                </MaxWidthWrapper>
            );
        }
        return (
            <MaxWidthWrapper minHeight={DEFAULT_EVENT_CARD_LIST_HEIGHT}>
                <EventCardsList sortedEvents={sortedEvents} loading={eventsLoading} />
            </MaxWidthWrapper>
        );
    }, [eventsLoading, league, sortedEvents]);

    const listOfPlayersWithEvents: PlayerWithTeamAndEvent[] = useMemo(
        () =>
            sortedPlayers
                .map(player => {
                    const eventInfo = sortedEvents.find(event => {
                        const eventPlayers = eventUtils.getAllPlayers(event);
                        return eventPlayers.some(p => p.id === player.id);
                    });
                    return eventInfo
                        ? {
                              ...player,
                              event: eventInfo,
                              league: player.league,
                              sport: player.sport,
                          }
                        : null;
                })
                .filter(Boolean),
        [sortedEvents, sortedPlayers]
    );
    const { openPlayerProfile } = useOpenPlayerProfile(listOfPlayersWithEvents);

    const openPlayerProfileWrapper = useCallback(
        (playerId: string) => {
            // on web we redirect user to player profile, not the single player profile sheet
            if (!isWeb && openSinglePlayerProfile) {
                const foundPlayer = listOfPlayersWithEvents.find(player => player.id === playerId);
                if (!foundPlayer) {
                    return;
                }
                openSinglePlayerProfile(foundPlayer, playerProjectionFilter);
                return;
            }
            openPlayerProfile(playerId, playerProjectionFilter);
        },
        [listOfPlayersWithEvents, openPlayerProfile, openSinglePlayerProfile, playerProjectionFilter]
    );

    const renderPlayerItem = useCallback(
        ({
            item,
            index,
            ignoreIndexes = 0,
        }: {
            item: PlayerWithTeamAndEvent;
            index: number;
            ignoreIndexes?: number;
        }): ReactElement | null => {
            if (Filters.Projection === selectedFilter) {
                return (
                    <PlayerTileRow
                        key={`player-${item.id}-${playerProjectionFilter}-${index}`}
                        openPlayerProfile={openPlayerProfileWrapper}
                        makeSelection={makeSelection}
                        removeSelection={removeSelection}
                        analyticsTag={'ProjectionsTab'}
                        index={index - ignoreIndexes}
                        sortedPlayers={sortedPlayers}
                        hideGameTime={hideGameTime}
                    />
                );
            }

            return (
                <MaxWidthWrapper>
                    <Box pl={'s14'} pr={'s16'}>
                        <PlayerItem
                            eventInfo={item.event}
                            player={item}
                            key={`player-${item.id}`}
                            filter={selectedFilter}
                            testID={sortedPlayers.findIndex(it => it.id === item.id).toString()}
                            makeSelection={makeSelection}
                            removeSelection={removeSelection}
                            analyticsTag={'PlayersTab'}
                            hideGameTime={hideGameTime}
                            openPlayerProfile={openPlayerProfileWrapper}
                        />
                    </Box>
                    <LineSeparator style={[common.separator, styles.marginHorizontal]} />
                </MaxWidthWrapper>
            );
        },
        [
            selectedFilter,
            sortedPlayers,
            makeSelection,
            removeSelection,
            hideGameTime,
            openPlayerProfileWrapper,
            playerProjectionFilter,
        ]
    );

    return {
        renderPlayerItem,
        renderFilters,
        renderEventCards,
        renderLeagueFooter,
    };
};

const styles = StyleSheet.create({
    marginHorizontal: {
        marginHorizontal: 16,
    },
});
